"use client";
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const PURPLE_LIGHT = '#a5b4fc';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function SearchIcon({ size = 18, color = PURPLE }: { size?: number; color?: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
            <circle cx="14" cy="14" r="8" stroke={color} strokeWidth="2.4" />
            <path d="M20 20l7 7" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
        </svg>
    );
}

/* Typewriter search bar that cycles through real search queries, then reveals
   the sponsored result ranking first — the core idea of Google Ads, staged live. */
const QUERIES = [
    'best digital marketing agency',
    'google ads management services',
    'ppc agency near me',
];

function LiveSearchDemo() {
    const [queryIndex, setQueryIndex] = useState(0);
    const [text, setText] = useState('');
    const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting'>('typing');

    useEffect(() => {
        const current = QUERIES[queryIndex];
        let timeout: ReturnType<typeof setTimeout>;

        if (phase === 'typing') {
            if (text.length < current.length) {
                timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), 55);
            } else {
                timeout = setTimeout(() => setPhase('pausing'), 1400);
            }
        } else if (phase === 'pausing') {
            timeout = setTimeout(() => setPhase('deleting'), 1000);
        } else {
            if (text.length > 0) {
                timeout = setTimeout(() => setText(text.slice(0, -1)), 28);
            } else {
                setQueryIndex((i) => (i + 1) % QUERIES.length);
                setPhase('typing');
            }
        }
        return () => clearTimeout(timeout);
    }, [text, phase, queryIndex]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: 'easeOut' }}
            className="w-full flex flex-col gap-4"
        >
            <div
                className="relative flex items-center gap-2.5 rounded-full px-5 py-3"
                style={{ background: CARD, boxShadow: '0 30px 70px -24px rgba(0,0,0,0.55)' }}
            >
                <SearchIcon size={17} color={PURPLE} />
                <span className="text-sm md:text-base truncate" style={{ fontFamily: MANROPE, color: INK }}>
                    {text}
                    <motion.span
                        animate={{ opacity: [1, 1, 0, 0] }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="inline-block w-[2px] h-5 ml-0.5 align-middle"
                        style={{ background: PURPLE }}
                    />
                </span>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.9, ease: 'easeOut' }}
                className="rounded-2xl p-4 md:p-5 flex items-start justify-between gap-4"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)' }}
            >
                <div className="flex flex-col gap-1.5 text-left min-w-0">
                    <div className="flex items-center gap-2">
                        <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: LIME, color: INK, fontFamily: CLASH, letterSpacing: '0.04em' }}
                        >
                            Ad
                        </span>
                        <span className="text-xs font-mono truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            yourbusiness.com
                        </span>
                    </div>
                    <p className="text-sm md:text-base font-semibold leading-snug" style={{ fontFamily: MANROPE, color: PURPLE_LIGHT }}>
                        Get Qualified Leads Today — Book a Free Consult
                    </p>
                    <p className="text-xs md:text-sm leading-snug" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.45)' }}>
                        Trusted by growing businesses. High-intent traffic, tracked to revenue.
                    </p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 shrink-0 pt-1">
                    <motion.span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: LIME }}
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <span className="text-[10px] font-mono uppercase tracking-wider whitespace-nowrap" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        position 1
                    </span>
                </div>
            </motion.div>
        </motion.div>
    );
}

const tools = [
    { src: '/logos/google-ads.svg', alt: 'Google Ads' },
    { src: '/logos/analytics.svg', alt: 'Google Analytics' },
    { src: '/logos/search-console.svg', alt: 'Google Search Console' },
];

export default function GoogleAdsHero() {
    const { openModal } = useContactModal();

    return (
        <section
            className="relative overflow-hidden"
            style={{ background: INK, borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}
        >
            {/* dot grid texture, lime on ink instead of purple on white */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />

            {/* two soft washes instead of one, both centered behind the copy */}
            <div
                className="absolute pointer-events-none"
                style={{
                    top: '-14%',
                    left: '50%',
                    transform: 'translateX(-62%)',
                    width: 720,
                    height: 620,
                    background: 'radial-gradient(circle, rgba(55,48,163,0.5) 0%, transparent 68%)',
                    borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                }}
            />
            <div
                className="absolute pointer-events-none"
                style={{
                    bottom: '-20%',
                    right: '8%',
                    width: 480,
                    height: 420,
                    background: 'radial-gradient(circle, rgba(206,255,26,0.12) 0%, transparent 70%)',
                    borderRadius: '58% 42% 37% 63% / 44% 41% 59% 56%',
                }}
            />

            <div className="relative max-w-[1120px] mx-auto px-6 md:px-16 pt-40 pb-20 flex flex-col items-center text-center gap-5">

                {/* Breadcrumb */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-2 text-xs md:text-sm font-medium"
                    style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}
                >
                    <Link href="/services/performance-marketing" className="hover:underline" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        Performance Marketing
                    </Link>
                    <span>/</span>
                    <span style={{ color: LIME }}>Google Ads Management</span>
                </motion.div>

                {/* H1 */}
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="text-3xl md:text-4xl lg:text-[3rem] font-bold leading-[1.12] tracking-tight max-w-4xl"
                    style={{ fontFamily: CLASH, color: '#ffffff' }}
                >
                    Every Search Is an{' '}
                    <span style={{ color: LIME }}>Auction</span>
                    . We Make Sure You Win It.
                </motion.h1>

                {/* Description */}
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.22 }}
                    className="text-base md:text-lg leading-relaxed max-w-3xl"
                    style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.01em' }}
                >
                    Lead Workz manages Google Ads campaigns around search intent, audience behaviour, creative and measurable outcomes — turning paid traffic into qualified leads, customers and revenue.
                </motion.p>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.32 }}
                    className="pt-1"
                >
                    <button
                        onClick={openModal}
                        className="inline-flex items-center justify-center px-7 py-3.5 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105"
                        style={{ background: LIME, color: INK, fontFamily: CLASH, letterSpacing: '0.08em' }}
                    >
                        Get a Google Ads Strategy →
                    </button>
                </motion.div>

                {/* Tool trust row */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="flex items-center gap-4 pt-1"
                >
                    <span
                        className="text-xs font-semibold uppercase tracking-[0.1em]"
                        style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.35)' }}
                    >
                        Managed with
                    </span>
                    <div className="flex items-center gap-3">
                        {tools.map((t) => (
                            <div
                                key={t.alt}
                                className="w-8 h-8 rounded-lg flex items-center justify-center p-1.5 opacity-60 hover:opacity-100 transition-opacity duration-300"
                                style={{ background: 'rgba(255,255,255,0.06)' }}
                            >
                                <Image src={t.src} alt={t.alt} width={24} height={24} className="w-full h-full object-contain" />
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Live search demo, centered below the fold */}
                <div className="w-full max-w-[620px] pt-4">
                    <LiveSearchDemo />
                </div>
            </div>
        </section>
    );
}
